import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import Icon from '../common/Icons';
import UserForm from './UserForm';
import UserDetails from './UserDetails';
import UserRowActionMenu from './UserRowActionMenu';
import { useRole } from '../../hooks/useRole';
import apiService from '../../services/api';
import SiteService from '../../services/siteService';
import { AdminPageHeader } from './ui';
import {
  formatUserDisplayName,
  formatUserMetaLine,
  formatWorkSitesSummary,
  getUserRoleLabel,
  userRoleCssKey
} from './userListUtils';
import './UserManagement.css';
import './AdminPortal.css';

const UserManagement = () => {
  const { toast } = useOutletContext() || { toast: () => {} };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [actionMenuUser, setActionMenuUser] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [workSites, setWorkSites] = useState([]);

  const { canManageUsers } = useRole();

  useEffect(() => {
    const loadSites = async () => {
      try {
        const res = await SiteService.getActiveSites();
        if (res.success) setWorkSites(res.data || []);
      } catch {
        setWorkSites([]);
      }
    };
    loadSites();
  }, []);

  const closeActionMenu = useCallback(() => {
    setActionMenuUser(null);
    setActionMenuAnchor(null);
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/admin/users', {
        params: { page, limit, search }
      });
      if (response.success) {
        const usersPayload = response?.data?.users ?? [];
        const paginationPayload = response?.data?.pagination ?? {
          total: usersPayload.length,
          totalPages: 1
        };
        setUsers(usersPayload);
        setPagination({
          total: paginationPayload.total || 0,
          totalPages: paginationPayload.totalPages || 1
        });
      } else {
        toast('danger', response.message || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (requestError) {
      console.error('Erreur lors du chargement des utilisateurs:', requestError);
      toast('danger', 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }, [limit, page, search, toast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsEditing(false);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditing(true);
    setShowUserForm(true);
  };

  const handleViewUser = async (user) => {
    try {
      setFormLoading(true);
      const res = await apiService.get(`/admin/users/${user.id}`);
      if (res.success && res.data?.user) {
        setSelectedUser(res.data.user);
      } else {
        setSelectedUser(user);
      }
      setShowUserDetails(true);
    } catch {
      setSelectedUser(user);
      setShowUserDetails(true);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      setFormLoading(true);
      const response = await apiService.put(`/admin/users/${userId}/status`, {
        is_active: isActive
      });
      if (response.success) {
        await loadUsers();
        toast('success', isActive ? 'Utilisateur activé.' : 'Utilisateur désactivé.');
      } else {
        toast('danger', response.message || 'Erreur lors de la mise à jour du statut');
      }
    } catch (requestError) {
      console.error('Erreur lors de la mise à jour du statut:', requestError);
      toast('danger', requestError.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setFormLoading(false);
      closeActionMenu();
    }
  };

  const handleDeleteUser = async (userId, { confirmed = false } = {}) => {
    const user = users.find((item) => item.id === userId);
    const label = user?.username || user?.email || `#${userId}`;
    if (!confirmed && !window.confirm(`Supprimer l'utilisateur ${label} ?`)) return;

    try {
      setFormLoading(true);
      const response = await apiService.delete(`/admin/users/${userId}`);
      if (response.success) {
        await loadUsers();
        toast('success', 'Utilisateur supprimé avec succès.');
      } else {
        toast('danger', response.message || 'Erreur lors de la suppression');
      }
    } catch (requestError) {
      console.error('Erreur lors de la suppression:', requestError);
      toast('danger', "Erreur lors de la suppression de l'utilisateur");
    } finally {
      setFormLoading(false);
      closeActionMenu();
    }
  };

  const openActionMenu = (event, user) => {
    if (actionMenuUser?.id === user.id && actionMenuAnchor) {
      closeActionMenu();
      return;
    }
    setActionMenuAnchor(event.currentTarget);
    setActionMenuUser(user);
  };

  const handleSaveUser = async (userData, userId) => {
    try {
      setFormLoading(true);
      let response;
      
      if (userId) {
        // Modification
        response = await apiService.put(`/admin/users/${userId}`, userData);
      } else {
        // Création
        response = await apiService.post('/admin/users', userData);
      }
      
      if (response.success) {
        await loadUsers();
        toast('success', userId ? 'Utilisateur modifié avec succès.' : 'Utilisateur créé avec succès.');
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (requestError) {
      console.error('Erreur lors de la sauvegarde:', requestError);
      return { success: false, message: requestError.message || 'Erreur lors de la sauvegarde' };
    } finally {
      setFormLoading(false);
    }
  };

  const hasUsers = users.length > 0;
  const pageLabel = useMemo(
    () => `Page ${page}/${Math.max(1, pagination.totalPages)}`,
    [page, pagination.totalPages]
  );
  const workSiteNameById = useMemo(
    () =>
      workSites.reduce((acc, site) => {
        acc[String(site.id)] = site.name;
        return acc;
      }, {}),
    [workSites]
  );

  return (
    <div className="adm-dash user-management-v2">
      <AdminPageHeader
        badge="Administration"
        title="Gestion des utilisateurs"
        description="Comptes, rôles et statuts — recherche et actions groupées."
        actions={
          canManageUsers() ? (
            <button type="button" className="users-create-btn" onClick={handleCreateUser}>
              <Icon name="plus" size={14} />
              <span>Créer utilisateur</span>
            </button>
          ) : null
        }
      />
      <section className="users-shell adm-panel adm-panel--flush">
          <div className="users-toolbar">
            <label className="users-search-wrap">
              <Icon name="search" size={16} className="users-search-icon" />
              <input
                className="users-search-input"
                placeholder="Nom, username, email…"
                aria-label="Rechercher un utilisateur"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </label>

            <div className="users-toolbar-meta">
              <span>{pagination.total} utilisateurs</span>
              <span>{pageLabel}</span>
            </div>
          </div>

          {loading ? (
            <div className="users-state">
              <Spinner animation="border" />
              <p>Chargement des utilisateurs...</p>
            </div>
          ) : !hasUsers ? (
            <div className="users-state">
              <div className="users-state__icon" aria-hidden>
                <Icon name={search.trim() ? 'search' : 'users'} size={28} />
              </div>
              <h6>{search.trim() ? 'Aucun résultat' : 'Aucun utilisateur'}</h6>
              <p>
                {search.trim()
                  ? `Aucun compte ne correspond à « ${search.trim()} ». Essayez un autre nom ou identifiant.`
                  : 'Commencez par créer le premier compte utilisateur de la plateforme.'}
              </p>
              {canManageUsers() && !search.trim() && (
                <button type="button" className="users-create-btn users-state__cta" onClick={handleCreateUser}>
                  <Icon name="userPlus" size={14} />
                  <span>Créer un utilisateur</span>
                </button>
              )}
            </div>
          ) : (
            <div className="users-table-wrap adm-mobile-table-wrap">
              <table className="users-table users-table--compact adm-mobile-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Affectation</th>
                    <th className="users-table__actions-col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const displayName = formatUserDisplayName(user);
                    const metaLine = formatUserMetaLine(user);
                    const sites = formatWorkSitesSummary(
                      user.work_site_ids,
                      workSiteNameById,
                      user.role
                    );

                    return (
                      <tr
                        key={user.id}
                        className={[
                          !user.is_active && 'users-row--inactive',
                          actionMenuUser?.id === user.id && 'adm-table-row--menu-open'
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <td className="users-cell-identity adm-mobile-card-head" data-label="Utilisateur">
                          <div className="users-identity">
                            <div className="users-identity__head">
                              <span className="users-identity__name">{displayName}</span>
                              <span
                                className={`users-status-dot ${user.is_active ? 'is-active' : 'is-inactive'}`}
                                title={user.is_active ? 'Actif' : 'Inactif'}
                                aria-label={user.is_active ? 'Actif' : 'Inactif'}
                              />
                            </div>
                            {metaLine ? (
                              <span className="users-identity__meta">{metaLine}</span>
                            ) : (
                              <span className="users-identity__meta users-identity__meta--muted">
                                {!user.username ? 'Username manquant' : 'Aucun contact renseigné'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="users-cell-assignment" data-label="Affectation">
                          <div className="users-assignment">
                            <span
                              className={`users-role-pill users-role-pill--${userRoleCssKey(user.role)}`}
                            >
                              {getUserRoleLabel(user.role)}
                            </span>
                            <span
                              className={`users-sites-line ${
                                sites.warn ? 'users-sites-line--warn' : sites.empty ? 'users-sites-line--muted' : ''
                              }`}
                              title={sites.title}
                            >
                              {sites.text}
                            </span>
                          </div>
                        </td>
                        <td className="users-table__actions-col adm-mobile-card-actions" data-label="Actions">
                          <button
                            type="button"
                            className={`users-action-btn users-action-btn--icon adm-action-btn adm-mobile-action-btn ${
                              actionMenuUser?.id === user.id ? 'is-open' : ''
                            }`}
                            disabled={formLoading}
                            onClick={(event) => openActionMenu(event, user)}
                            aria-label={`Actions pour ${displayName}`}
                            aria-expanded={actionMenuUser?.id === user.id}
                            aria-haspopup="menu"
                            title="Actions"
                          >
                            <Icon name="ellipsis-vertical" size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {hasUsers && (
          <footer className="users-footer">
            <span className="users-footer__info">{pageLabel} · {pagination.total} au total</span>
            <div className="users-footer__nav">
              <button
                type="button"
                className="users-page-btn"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <Icon name="chevron-left" size={14} />
                <span>Précédent</span>
              </button>
              <button
                type="button"
                className="users-page-btn"
                disabled={page >= pagination.totalPages || loading}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              >
                <span>Suivant</span>
                <Icon name="chevron-right" size={14} />
              </button>
            </div>
          </footer>
          )}
        </section>

      <UserRowActionMenu
        anchorEl={actionMenuAnchor}
        user={actionMenuUser}
        onClose={closeActionMenu}
        onView={handleViewUser}
        onEdit={handleEditUser}
        onToggleStatus={handleToggleUserStatus}
        onDelete={handleDeleteUser}
        disabled={formLoading}
      />

      <UserForm
        show={showUserForm}
        onHide={() => setShowUserForm(false)}
        onSave={handleSaveUser}
        user={selectedUser}
        isEditing={isEditing}
        loading={formLoading}
        workSites={workSites}
      />

      <UserDetails
        show={showUserDetails}
        onHide={() => setShowUserDetails(false)}
        user={selectedUser}
        onEdit={handleEditUser}
        onDelete={handleDeleteUser}
        onToggleStatus={handleToggleUserStatus}
      />
    </div>
  );
};

export default UserManagement;